import { err, ok, type Result } from "./result";

const DAY_MS = 24 * 60 * 60 * 1000;

const AU_TIME_ZONE = "Australia/Perth";

const AU_DATE_FORMATTER = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: AU_TIME_ZONE,
});

const AU_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("en-AU", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: AU_TIME_ZONE,
});

export type DateInputError = {
  readonly kind: "invalidDateInput";
  readonly reason: "format" | "utcHour" | "nonexistentDate";
  readonly value: string;
};

type DateParts = {
  readonly year: number;
  readonly month: number;
  readonly day: number;
};

const DATE_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const invalidDateInput = (
  reason: DateInputError["reason"],
  value: string,
): DateInputError => ({ kind: "invalidDateInput", reason, value });

const parseDateParts = (value: string): Result<DateParts, DateInputError> => {
  const match = DATE_INPUT_RE.exec(value);
  if (match === null) return err(invalidDateInput("format", value));

  const [, yearRaw, monthRaw, dayRaw] = match;
  if (yearRaw === undefined || monthRaw === undefined || dayRaw === undefined) {
    return err(invalidDateInput("format", value));
  }

  return ok({
    year: Number(yearRaw),
    month: Number(monthRaw),
    day: Number(dayRaw),
  });
};

const isUtcHour = (value: number): boolean =>
  Number.isInteger(value) && value >= 0 && value <= 23;

export const formatAuDate = (ms: number): string =>
  AU_DATE_FORMATTER.format(new Date(ms));

export const formatNullableAuDate = (
  ms: number | null | undefined,
  emptyLabel = "No due date",
): string => (ms == null ? emptyLabel : formatAuDate(ms));

export const formatAuDateTime = (ms: number): string =>
  AU_DATE_TIME_FORMATTER.format(new Date(ms));

export const formatNullableAuDateTime = (
  ms: number | null | undefined,
  emptyLabel = "-",
): string => (ms == null ? emptyLabel : formatAuDateTime(ms));

export const dateInputValueFromMs = (ms: number | null | undefined): string =>
  ms == null ? "" : new Date(ms).toISOString().slice(0, 10);

export const parseDateInputAtUtcHour = (
  value: string,
  utcHour: number,
): Result<number, DateInputError> => {
  const cleaned = value.trim();
  const parts = parseDateParts(cleaned);
  if (!parts.ok) return parts;
  if (!isUtcHour(utcHour)) return err(invalidDateInput("utcHour", String(utcHour)));

  const utc = Date.UTC(parts.value.year, parts.value.month - 1, parts.value.day, utcHour);
  const date = new Date(utc);
  if (
    date.getUTCFullYear() !== parts.value.year ||
    date.getUTCMonth() !== parts.value.month - 1 ||
    date.getUTCDate() !== parts.value.day ||
    date.getUTCHours() !== utcHour
  ) {
    return err(invalidDateInput("nonexistentDate", cleaned));
  }

  return ok(utc);
};

export const parseOptionalDateInputAtUtcHour = (
  value: string,
  utcHour: number,
): Result<number | null, DateInputError> => {
  const cleaned = value.trim();
  if (cleaned === "") return ok(null);
  return parseDateInputAtUtcHour(cleaned, utcHour);
};

export const parseOptionalDateInput = (
  value: string,
): Result<number | null, DateInputError> => parseOptionalDateInputAtUtcHour(value, 12);

export type DueDateState =
  | { readonly kind: "none" }
  | { readonly kind: "dueNow"; readonly days: number }
  | { readonly kind: "upcoming"; readonly days: number };

export type DueDateLabelStyle = "short" | "long";

export const daysUntil = (targetMs: number, nowMs: number): number => {
  const days = Math.ceil((targetMs - nowMs) / DAY_MS);
  return Object.is(days, -0) ? 0 : days;
};

export const dueDateState = (targetMs: number | null, nowMs: number): DueDateState => {
  if (targetMs === null) return { kind: "none" };
  const days = daysUntil(targetMs, nowMs);
  return days <= 0 ? { kind: "dueNow", days } : { kind: "upcoming", days };
};

export const dueDateLabel = (
  targetMs: number | null,
  nowMs: number,
  style: DueDateLabelStyle = "long",
): string => {
  const state = dueDateState(targetMs, nowMs);
  switch (state.kind) {
    case "none":
      return "No due date";
    case "dueNow":
      return "Due now";
    case "upcoming":
      return style === "short" ? String(state.days) + "d" : String(state.days) + " days";
  }
};

export const isTodayOrFuture = (targetMs: number, nowMs: number): boolean =>
  daysUntil(targetMs, nowMs) >= 0;
